/**
 * Selección del paso de tiempo adaptativo (S9, límites L1 a L8 de docs/theory/simulation.md).
 * El paso es el mínimo de los límites activos, con suelo en `userStep / 20`, y el
 * próximo evento programado se alcanza exactamente.
 */

/** Factor máximo de crecimiento respecto al paso anterior (L6). */
export const GROWTH_FACTOR = 1.5;

/** Fracción del paso del usuario que actúa como suelo del paso (1/20). */
export const MIN_STEP_FRACTION = 1 / 20;

/** Divisor del paso del usuario sobre la varilla (L1). */
const ROD_STEP_DIVISOR = 5;

/** Mínimo de pasos para recorrer la varilla (L5). */
const ROD_MIN_STEPS = 10;

/** Qué límite decidió el paso. */
export type StepLimiter =
  | 'user'
  | 'angle'
  | 'roll'
  | 'oscillation'
  | 'rod'
  | 'growth'
  | 'extra'
  | 'event'
  | 'floor';

/** Resultado: paso elegido y límite que lo decidió. */
export interface StepChoice {
  readonly dt: number;
  readonly limiter: StepLimiter;
}

/** Datos de entrada para elegir el paso. */
export interface StepLimits {
  /** Paso elegido por el usuario, s (L1). */
  readonly userStep: number;
  /** Cohete aún en la varilla (o en el suelo): el paso de usuario se divide por 5. */
  readonly onRod: boolean;
  /** Longitud de la varilla, m (L5). */
  readonly rodLength: number;
  /** Módulo de la velocidad, m/s. */
  readonly speed: number;
  /** Tiempo hasta el próximo evento programado, s (puede ser infinito) (L8). */
  readonly timeToEvent: number;
  /** Giro lateral máximo por paso, rad (L2). */
  readonly maxAngleStep: number;
  /** Velocidad angular lateral, rad/s (L2). */
  readonly lateralRate: number;
  /** Giro de rodadura máximo por paso, rad (L3). */
  readonly maxRollStep: number;
  /** Velocidad de rodadura, rad/s; el signo es indiferente (L3). */
  readonly rollRate: number;
  /** Frecuencia natural de cabeceo ω_n, rad/s (L4); 0 si no aplica. */
  readonly pitchFrequency: number;
  /** Pasos por periodo de la oscilación de cabeceo (L4). */
  readonly stepsPerPeriod: number;
  /** Paso anterior, s (L6); ausente en el primer paso. */
  readonly previousStep?: number;
  /** Cota adicional, p. ej. la del paracaídas (L7), s. */
  readonly extraLimit?: number;
}

/** Elige el paso de tiempo como el mínimo de los límites activos. */
export function selectTimeStep(l: StepLimits): StepChoice {
  let dt = l.onRod ? l.userStep / ROD_STEP_DIVISOR : l.userStep;
  let limiter: StepLimiter = 'user';

  // Cada candidato sustituye al actual solo si es estrictamente menor.
  const consider = (candidate: number, id: StepLimiter): void => {
    if (candidate < dt) {
      dt = candidate;
      limiter = id;
    }
  };

  const lateral = Math.abs(l.lateralRate);
  if (lateral > 0) consider(l.maxAngleStep / lateral, 'angle');

  const roll = Math.abs(l.rollRate);
  if (roll > 0) consider(l.maxRollStep / roll, 'roll');

  if (l.pitchFrequency > 0 && l.stepsPerPeriod > 0) {
    consider((2 * Math.PI) / (l.stepsPerPeriod * l.pitchFrequency), 'oscillation');
  }

  if (l.onRod && l.speed > 0) consider(l.rodLength / (ROD_MIN_STEPS * l.speed), 'rod');

  if (l.previousStep !== undefined && l.previousStep > 0) {
    consider(GROWTH_FACTOR * l.previousStep, 'growth');
  }

  if (l.extraLimit !== undefined) consider(l.extraLimit, 'extra');

  // Suelo: un veinteavo del paso del usuario (sin la división de la varilla).
  const floor = l.userStep * MIN_STEP_FRACTION;
  if (dt < floor) {
    dt = floor;
    limiter = 'floor';
  }

  // Evento: se alcanza exactamente si cae dentro del paso o apenas después
  // (a menos de un suelo de distancia), para no dejar un paso diminuto.
  if (l.timeToEvent <= dt + floor) {
    return { dt: Math.max(0, l.timeToEvent), limiter: 'event' };
  }

  return { dt, limiter };
}
